// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectInspector, chromeLight } from 'react-inspector';

// Import Pages
import DataTable from '../DataTable/DataTable';
import TransformList from '../TransformList/TransformList';

import Info from "../../../../../home/pages/automs/autom/editAction/components/Info";
import DeleteConfirm from "../../../../../components/DataTable/DeleteConfirm";

// Import Material UI
import CircularProgress from '@material-ui/core/CircularProgress';
import SelectAllOutlinedIcon from '@material-ui/icons/SelectAllOutlined';
import { Button } from '@material-ui/core';

// Import Icons
import RefreshIcon from '../../../../../assets/icons/refresh-icon.svg';
import InformationIcon from '../../../../../assets/icons/information-icon.svg';
import ResetIcon from '../../../../../assets/icons/headerIcons/reset-icon-new.svg';

function Preview({ previewData, transformData, loading, reFetchData, outputVariableName, onChangeTransformData, transformKeys, resetTransforms, clearTransformData }) {
    const [showDataTable, setShowDataTable] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [showTransforms, setShowTransforms] = useState(false);

    const { t } = useTranslation();
    const label = "CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.DATA-TRANSFORM.";


    const handleKeyClick = (e, { data, path }) => {
        e.stopPropagation();
        e.preventDefault();
        let dataType = findDataType(data);
        let dataPath = getPath(path);
        onChangeTransformData(dataPath, dataType);
    };

    const findDataType = (data) => {
        let type = "string";

        if (data !== null) {
            if (Array.isArray(data)) {
                if (data.some(element => Array.isArray(element))) {
                    type = "data table";
                } else {
                    type = "array";
                }
            } else if (data instanceof Date) {
                type = "date";
            } else if (typeof data === 'string') {
                type = "string";
            } else if (typeof data === 'number') {
                type = "number";
            } else if (typeof data === 'boolean') {
                type = "boolean";
            } else if (typeof data === 'object') {
                type = "object";
            }
        } else {
            type = "object";
        }

        return type;
    }

    const getPath = (v) => {
        let path = "";
        const arr = v.split('.');
        for (const p of arr) {
            if (p === "$") {
                path += outputVariableName;
                continue;
            }
            path += `[${p}]`
        }

        return path;
    }

    const nodeRenderer = ({ depth, name, data, isNonenumerable, path }) => {

        const renderData = (data) => {
            if (data !== null) {
                if (Array.isArray(data)) {
                    return <span className='transform-gray-color m-l-5'>{"[ ]"} {data.length} item</span>
                }
                if (typeof data === 'object') {
                    return <span className='transform-gray-color  m-l-5'>{"{ } " + Object.keys(data).length + " keys"} </span>
                }
            }

            if (typeof data === 'string') {
                return <span className='m-l-5' style={{ color: "#c5221f" }}>"{data.toString()}"</span>;
            }
            return <span className='m-l-5' style={{ color: data ? "#1a73e8" : "#B7B7B7" }}>{data ? data.toString() : "null"}</span>;
        };

        if (!name) return <span className='transform-gray-color  m-l-5' style={{ fontSize: "14px" }}>{previewData.type === "object" ? "{ ... }" : "[ ... ]"}</span>;


        const p = getPath(path);
        return (
            <span onClick={(e) => handleKeyClick(e, { data, path })}>
                <span className={transformData?.path === p ? "selected-key" : (transformKeys.find(x => x === p) ? "transform-key" : "")} style={{ color: "#9D5B9B", cursor: "pointer" }}>{name}</span>:  {renderData(data)}
            </span>
        );
    };

    const customTheme = {
        ...chromeLight, // chromeDark temasını baz al
        BASE_BACKGROUND_COLOR: '#F3F3F3', // Arka plan rengini değiştir
        BASE_COLOR: 'black', // Metin rengini değiştir
        TREENODE_FONT_SIZE: '12px',
        TREENODE_LINE_HEIGHT: '1.5',
        ARROW_COLOR: "#5F5F5F"
        // TREENODE_PADDING_LEFT: '20px',
        // ARROW_FONT_SIZE: '1.1em',
        // ARROW_MARGIN_RIGHT: '3px',
        // ARROW_ANIMATION_DURATION: '0.2s',
    };


    const renderDataTable = () => {
        if (!previewData.data) {
            return undefined
        }

        let v = previewData.data;
        if (!Array.isArray(previewData.data) && previewData.data[outputVariableName]) {
            v = previewData.data[outputVariableName];
        }

        return v.slice(0, Math.min(5, v.length)).map((row, index) => {
            const backgroundColor = index === 0 ? '#4F6E90' : '#F4F4F4';
            const color = index === 0 ? '#F8F8F8' : '#4A4A4A';

            return (
                <tr key={index} style={{ backgroundColor, color }}>
                    {row.slice(0, Math.min(2, row.length)).map((cell, cellIndex) => {
                        return <td className={index === 0 ? "data-table-first-header-td" : ""} key={cellIndex}>{cell}</td>;
                    })}
                </tr>
            );
        });
    };

    const showPreviewData = () => {

        if (previewData && previewData.data) {
            switch (previewData.type) {
                case "string":
                case "number":
                case "date":
                case "boolean":
                    return <div className={`clear-text-content type-color-${previewData.type} ${transformData?.path === outputVariableName ? "selected-key f-s-11" : (transformKeys.find(x => x === outputVariableName) ? "transform-key f-s-11" : "")}`} onClick={() => onChangeTransformData(outputVariableName, previewData.type)}>{previewData.data}</div>
                case "object":
                case "array":
                    return <div className='transform-preview-container'>
                        <ObjectInspector data={previewData.data} nodeRenderer={nodeRenderer} theme={customTheme}
                            style={{ backgroundColor: '#F3F3F3', color: 'black' }} expandLevel={1} />
                    </div>
                case "data table":
                    return (
                        <div className='transform-preview-container'>
                            <div className='data-table-select-values' onClick={() => {
                                onChangeTransformData();
                                setShowDataTable(true);
                            }}>{t(label + "SELECT-VALUE")}</div>
                            <div className='transform-table-container overflow-hidden'>
                                <table className='transform-data-table-container' >
                                    <tbody>
                                        {renderDataTable()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                case "dictionary":
                    return <div></div>
                default:
                    break;
            }
        }


        return <div className="items-center-center full-height">
            <div className='empty-preview-message'>
                {t(label + "EMPTY-PREVIEW-MESSAGE")}
            </div>
        </div>

    }


    const saveDataTable = (p) => {
        onChangeTransformData(p, "string");
        setShowDataTable(false);
    }

    const saveTransformList = (p, t) => {
        onChangeTransformData(p, t);
        setShowTransforms(false);
    }

    const cancel = () => {
        setShowDataTable(false);
    }

    const selectCurrentVariable = () => {
        onChangeTransformData(outputVariableName, previewData.type)
    }

    return (
        <div className='preview-data-container'>
            <div className='items-center-between'>
                <div className='items-center padding-3'>
                    <div className='preview-text'> {t(label + "PREVIEW")}</div>
                    <Info value={t(label + "REFETCH-PREVIEW-DATA-INFO")} placement="bottom" delay={300}>
                        <img className='icon-cursor-pointer' onClick={!loading ? reFetchData : undefined} src={RefreshIcon} alt="action control icon" />
                    </Info>
                    <div className='select-all-icon-container' onClick={selectCurrentVariable}>
                        <Info value={t(label + "TRANSFORM-SOURCE-INFO")} placement="bottom" delay={300}>
                            <SelectAllOutlinedIcon style={{
                                color: transformData?.path === outputVariableName ? "rgb(57 231 86)" :
                                    (transformKeys.find(x => x === outputVariableName) ? "rgb(71 124 243)" : '#C0C3CD')
                            }} />
                        </Info>

                    </div>
                    {
                        previewData.type && (previewData.type === "array" || previewData.type === "object" || previewData.type === "data table") &&
                        <Info value={t(label + "TRANSFORMS-INFO")} placement="bottom" delay={300}>
                            <div className='m-l-15'>
                                <Button className='transforms-button' onClick={() => { clearTransformData(); setShowTransforms(true) }}>
                                    {t(label + 'TRANSFORMS')}
                                </Button>
                            </div>
                        </Info>
                    }
                </div>
                <Info value={t(label + "RESET-TRANSFORMS-INFO")} placement="bottom" delay={300}>
                    <img className="s-with-41 icon-cursor-pointer m-b-3" src={ResetIcon} alt="" onClick={() => transformKeys.length ? setShowResetConfirm(true) : undefined} />
                </Info>
                {/* <img className='icon-cursor-pointer' src={InformationIcon} alt="action control icon" /> */}
            </div >
            <div className={`preview-data-content ${(loading || !previewData || (previewData &&
                previewData.type !== "string" && previewData.type !== "number" && previewData.type !== "date" && previewData.type !== "boolean")) && "full-height"}`}>
                {
                    loading ?
                        <div className='items-center-center full-height'>
                            <CircularProgress className='loading-circular-process' />
                        </div>

                        :
                        showPreviewData()
                }
            </div>

            {
                showDataTable &&
                <DataTable previewData={previewData} outputVariableName={outputVariableName} transformKeys={transformKeys} cancel={cancel} save={saveDataTable} />
            }

            {
                showResetConfirm &&
                <DeleteConfirm setOpenDelete={setShowResetConfirm}
                    message={t(label + "RESET-CONFIRM-QUESTION")}
                    deleteFunc={resetTransforms} />
            }

            {
                showTransforms &&
                <TransformList previewData={previewData} outputVariableName={outputVariableName} transformKeys={transformKeys}
                    cancel={() => setShowTransforms(false)} save={saveTransformList} />
            }
        </div >
    )
}

export default Preview;